/**
 * useUsersWithRoles
 *
 * 역할 기반으로 사용자 목록을 조회합니다.
 * Admin, Moderator 등 특정 역할을 가진 사용자 필터링에 사용됩니다.
 */

import { useMany } from '@popow/infra/lib/supabase/query';
import type { Tables } from '@popow/infra/types/database.types';
import { parseUserProfileRow } from '../schema';

type UserProfileRow = Tables<'user_profiles'>;

interface UseUsersWithRolesOptions {
  /** 필터링할 역할 목록 (예: ['admin', 'moderator']) */
  roles?: string[];
  /** 검색 쿼리 (email, username, tiktok_handle) */
  search?: string;
  /** 페이지 크기 */
  pageSize?: number;
}

/**
 * 역할 기반 사용자 목록 조회
 *
 * @example
 * // Admin 사용자만 조회
 * const { data: adminUsers } = useUsersWithRoles({ roles: ['admin'] });
 *
 * @example
 * // Admin과 Moderator 검색
 * const { data: staffUsers } = useUsersWithRoles({
 *   roles: ['admin', 'moderator'],
 *   search: 'john'
 * });
 */
export function useUsersWithRoles(options?: UseUsersWithRolesOptions) {
  const { roles, search, pageSize = 50 } = options || {};

  return useMany({
    tableName: 'user_profiles',
    columns: '*',
    key: ['users', 'with-roles', roles, search],
    enabled: true,
    pageSize,
    trailingQuery: query => {
      let q = query.order('created_at', { ascending: false });

      // 검색 필터
      if (search) {
        q = q.or(
          `email.ilike.%${search}%,username.ilike.%${search}%,tiktok_handle.ilike.%${search}%`,
        );
      }

      // 역할 필터
      if (roles && roles.length > 0) {
        q = q.in('role', roles);
      }

      return q;
    },
    parseFn: (row: UserProfileRow) => parseUserProfileRow(row),
  });
}
